import { useStore } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';

import { handleUnexpectedError } from '@/app/errors';
import { useCurrentUser } from '@/app/hooks/use-current-user';
import { useAppForm } from '@/app/lib/tanstack-form';
import { updateTapEnrollmentSetting } from '@/app/network/firefly-authenticated-api';
import { Button, ButtonGroup } from '@/common/components/button';
import { FieldInfo, Label } from '@/common/components/fieldset';
import { Heading } from '@/common/components/heading';
import { SwitchField } from '@/common/components/switch';
import { Text } from '@/common/components/text';
import { getAccountLabel } from '@/shared/accounts';
import { TapTermsDialog } from '@/shared/components/tap-terms-dialog';
import { getCurrentUserQueryOptions } from '@/shared/user/queries';

const accountTapSettingsFormSchema = z.object({
  accounts: z.array(z.object({
    accountNumber: z.number(),
    enrollmentSetting: z.boolean()
  }))
});

export const Route = createFileRoute('/memberships/setup/$member-number/tap')({
  component: TapPage
});

function TapPage() {
  const { membership } = Route.useRouteContext();
  const { accounts } = useCurrentUser();
  const membershipAccounts = accounts.filter(account => account.member_number === membership.member_number && account.type != 'other');
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const accountsMap = new Map(membershipAccounts.map(account => [account.account_number, account]));

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [tapTermsDialogIsOpen, setTapTermsDialogIsOpen] = useState(false);

  const { mutateAsync } = useMutation({
    mutationFn: async (values: z.infer<typeof accountTapSettingsFormSchema>['accounts']) => {
      await Promise.all(
        values.map(account => updateTapEnrollmentSetting(
          account.accountNumber,
          account.enrollmentSetting
        ))
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getCurrentUserQueryOptions()['queryKey'] });
      await navigate({ to: '/memberships/setup/$member-number/newsletter', params: { 'member-number': membership.member_number.toString() } });
    },
    onError: error => handleUnexpectedError(error)
  });

  const form = useAppForm({
    defaultValues: {
      accounts: membershipAccounts.map(account => ({
        accountNumber: account.account_number,
        enrollmentSetting: false
      }))
    },
    onSubmit: async ({ value }) => {
      await mutateAsync(value.accounts);
    }
  });

  const isEnrollingAnyAccounts = useStore(form.store, state => state.values.accounts.some(it => it.enrollmentSetting));

  const onContinue = async () => {
    if (agreedToTerms || !isEnrollingAnyAccounts) {
      await form.handleSubmit();
    } else {
      setTapTermsDialogIsOpen(true);
    }
  };

  const onAgreeToTerms = async () => {
    setAgreedToTerms(true);

    await form.handleSubmit();

    setTapTermsDialogIsOpen(false);
  };

  return (
    <>
      <Heading>TAP Enrollment</Heading>
      <Text>Please select any accounts you would like to enroll in TAP.</Text>
      <Button
        href="https://www.talquinelectric.com/member-services/talquin-assistance-program-tap/"
        plain
        target="_blank"
      >
        What is TAP?
      </Button>

      <form className="grid w-full grid-cols-1 gap-8">
        <form.AppField name="accounts">
          {field => (
            field.state.value.map((accountValue, idx) => {
              const account = accountsMap.get(accountValue.accountNumber)!;

              return (
                <SwitchField key={idx}>
                  <Label>{getAccountLabel(account)}</Label>
                  <form.AppField name={`accounts[${idx}].enrollmentSetting`}>
                    {(subField) => {
                      return (
                        <>
                          <subField.FormSwitch />
                          <FieldInfo field={subField} />
                        </>
                      );
                    }}
                  </form.AppField>
                </SwitchField>
              );
            }))}
        </form.AppField>
        <ButtonGroup>
          <Button onClick={onContinue}>Continue</Button>
        </ButtonGroup>
      </form>

      <TapTermsDialog isOpen={tapTermsDialogIsOpen} onClose={setTapTermsDialogIsOpen} onAgreeToTerms={onAgreeToTerms} />
    </>
  );
}
